import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { authedDelete, authedPatch } from "@/lib/api";
import type { BookkeeperThreadRow } from "@releve/shared";

// Reads are direct + RLS-scoped (owner + org member); writes go through the backend.

/** List the current org's chat threads, most-recent first (direct RLS read). */
export function useBookkeeperThreads() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;

  return useQuery<BookkeeperThreadRow[]>({
    queryKey: ["bookkeeper-threads", orgId],
    queryFn: async () => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from("bookkeeper_threads")
        .select("id, title, last_message_at, created_at")
        .eq("organization_id", orgId)
        .order("last_message_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BookkeeperThreadRow[];
    },
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
}

export function useRenameThread() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadId, title }: { threadId: string; title: string }) => {
      if (!orgId) throw new Error("No organization selected");
      return authedPatch(`/api/v1/bookkeeper/orgs/${orgId}/threads/${threadId}`, { title });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookkeeper-threads", orgId] });
    },
  });
}

export function useDeleteThread() {
  const { selectedOrganization } = useOrganization();
  const orgId = selectedOrganization?.id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (threadId: string) => {
      if (!orgId) throw new Error("No organization selected");
      return authedDelete(`/api/v1/bookkeeper/orgs/${orgId}/threads/${threadId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookkeeper-threads", orgId] });
    },
  });
}
